import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Clock, Shield, Wrench, CheckCircle, Star, ArrowRight, Calendar, MapPin } from 'lucide-react';

const Home = () => {
  const [bookingForm, setBookingForm] = useState({
    name: '',
    address: '',
    issue: '',
    contact: ''
  });

  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const handleFormChange = (e) => {
    setBookingForm({
      ...bookingForm,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Create WhatsApp message with booking details
    const message = `🔧 *New TV Repair Booking*

📋 Customer Details:
━━━━━━━━━━━━━━━
👤 Name: ${bookingForm.name}
📱 Contact: ${bookingForm.contact}
📍 Address: ${bookingForm.address}

🛠️ TV Issue:
${bookingForm.issue}

━━━━━━━━━━━━━━━
Please confirm the service appointment.

- Garg Electronics`;

    const whatsappUrl = `https://wa.me/918299034722?text=${encodeURIComponent(message)}`;
    
    // Google Ads Conversion Tracking
    if (window.gtag) {
      window.gtag('event', 'conversion', {
        'send_to': 'AW-CONVERSION_ID/CONVERSION_LABEL',
        'event_category': 'Lead',
        'event_label': 'WhatsApp Booking',
        'value': 1.0,
        'currency': 'INR'
      });
    }
    
    // Open WhatsApp in new tab
    window.open(whatsappUrl, '_blank');
    
    // Show success message
    setIsFormSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setBookingForm({
        name: '',
        address: '',
        issue: '',
        contact: ''
      });
      setIsFormSubmitted(false);
    }, 3000);
  };

  const features = [
    {
      id: 'same-day-repair',
      icon: <Clock className="w-8 h-8" />,
      title: 'Same-Day Repair',
      description: '90% repairs completed within 3 hours at your doorstep'
    },
    {
      id: '120-day-warranty',
      icon: <Shield className="w-8 h-8" />,
      title: '120-Day Warranty',
      description: 'Every repair backed by our solid warranty guarantee'
    },
    {
      id: 'expert-technicians',
      icon: <Wrench className="w-8 h-8" />,
      title: 'Expert Technicians',
      description: 'Factory-trained & certified TV repair specialists. Background verified for your safety.'
    },
    {
      id: 'transparent-pricing',
      icon: <CheckCircle className="w-8 h-8" />,
      title: 'Transparent Pricing',
      description: 'Free diagnosis. No hidden charges. Ever.'
    }
  ];

  const commonIssues = [
    {
      id: 'broken-screen',
      title: 'Broken Screen / Cracked Display',
      description: 'Physical damage to TV screen with visible cracks, shattered glass, or dead pixels. Internal LCD/LED panel damage causing display malfunction. We replace damaged panels with A-grade components.',
      image: 'https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/l7qtmycz_1000035874.png',
      icon: '💥'
    },
    {
      id: 'tv-installation',
      title: 'TV Installation & Wall Mounting',
      description: 'Professional wall mounting service with proper cable management and optimal viewing angle setup. Secure installation ensuring TV safety and clean aesthetics. Includes bracket installation and leveling.',
      image: 'https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/pzxlqaco_1000035856.png',
      icon: '🔧'
    },
    {
      id: 'circuit-board-repair',
      title: 'Internal Circuit Board Repair',
      description: 'Motherboard and circuit board repair for power issues, no display, or TV not turning on. Board-level component replacement and soldering. Expert diagnosis of capacitor, IC, and transistor failures.',
      image: 'https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/mwsyxbv2_1000036486.jpg',
      icon: '⚡'
    },
    {
      id: 'smart-tv-software',
      title: 'Smart TV Software & Netflix Issues',
      description: 'Smart TV not connecting to WiFi, apps crashing, Netflix not working, slow performance. Software updates, OS reinstallation, app configuration. Complete Smart TV functionality restoration.',
      image: 'https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/03lscane_1000035845.png',
      icon: '📱'
    }
  ];

  const stats = [
    { id: 'years-exp', number: '30+', label: 'Years Experience' },
    { id: 'tvs-repaired', number: '10,000+', label: 'TVs Repaired' },
    { id: 'satisfaction', number: '95%', label: 'Customer Satisfaction' },
    { id: 'avg-time', number: '3 Hours', label: 'Average Fix Time' }
  ];

  const tvBrands = ['Samsung', 'LG', 'Sony', 'OnePlus', 'Xiaomi', 'TCL', 'Haier', 'Panasonic', 'Philips', 'Other'];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gray-50 py-16 md:py-24 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-gray-900 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                Trusted Since 1996 ⭐
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Kanpur's #1 TV Repair Experts
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8">
                LED • LCD • Smart TV • OLED Repairs at Your Doorstep. Same-day service across Kanpur. 30+ years of trusted excellence.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:9565711092"
                  className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 text-white text-lg font-semibold rounded-lg hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call: 9565711092
                </a>
                <a
                  href="https://wa.me/918299034722"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-green-600 text-white text-lg font-semibold rounded-lg hover:bg-green-700 transition-all shadow-lg hover:shadow-xl"
                >
                  WhatsApp Us
                </a>
              </div>
              <div className="mt-6 flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-gray-900 mr-2" />
                  Free Diagnosis
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-gray-900 mr-2" />
                  No Hidden Charges
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/l7qtmycz_1000035874.png"
                alt="Expert TV Repair Service in Kanpur - LED LCD Smart TV OLED Repair"
                loading="lazy"
                className="rounded-2xl shadow-2xl w-full object-cover border border-gray-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.id} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Service Form Section */}
      <section className="py-16 md:py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Book Your TV Repair Service
            </h2>
            <p className="text-lg text-gray-600">
              Fill the form below and we'll call you back within 30 minutes
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 md:p-10">
            {isFormSubmitted ? (
              <div className="text-center py-12">
                <div className="mb-6">
                  <CheckCircle className="w-20 h-20 text-green-600 mx-auto" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Service Booked Successfully! ✅</h3>
                <p className="text-gray-600 mb-4">WhatsApp par aapki booking details bhej di gayi hain.</p>
                <p className="text-sm text-gray-500">Our technician team will contact you shortly via WhatsApp or call.</p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={bookingForm.name}
                      onChange={handleFormChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-900 transition-colors"
                      placeholder="Enter your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact" className="block text-sm font-bold text-gray-900 mb-2">
                      Contact Number *
                    </label>
                    <input
                      type="tel"
                      id="contact"
                      name="contact"
                      value={bookingForm.contact}
                      onChange={handleFormChange}
                      required
                      pattern="[0-9]{10}"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-900 transition-colors"
                      placeholder="10-digit mobile number"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-bold text-gray-900 mb-2">
                    Your Address *
                  </label>
                  <textarea
                    id="address"
                    name="address"
                    value={bookingForm.address}
                    onChange={handleFormChange}
                    required
                    rows="2"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-900 transition-colors resize-none"
                    placeholder="Enter your complete address with area, Kanpur"
                  ></textarea>
                </div>

                <div>
                  <label htmlFor="issue" className="block text-sm font-bold text-gray-900 mb-2">
                    TV Problem / Issue Description *
                  </label>
                  <textarea
                    id="issue"
                    name="issue"
                    value={bookingForm.issue}
                    onChange={handleFormChange}
                    required
                    rows="4"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-gray-900 transition-colors resize-none"
                    placeholder="Describe the TV issue (e.g., screen not turning on, broken display, no sound, etc.)"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gray-900 text-white font-bold py-4 rounded-lg hover:bg-gray-800 transition-colors shadow-lg hover:shadow-xl flex items-center justify-center text-lg"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Service Now
                </button>

                <p className="text-sm text-gray-500 text-center">
                  By submitting, you agree to be contacted for service booking
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Common TV Issues with Images */}
      <section className="py-16 md:py-20 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common TV Problems We Fix
            </h2>
            <p className="text-lg text-gray-600">
              Expert solutions for every television issue
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {commonIssues.map((issue) => (
              <div key={issue.id} className="bg-gray-50 rounded-xl overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
                <div className="relative h-64">
                  <img
                    src={issue.image}
                    alt={`${issue.title} - TV Repair Service Kanpur`}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{issue.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{issue.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-20 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Garg Electronics?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Three decades of expertise, thousands of happy customers
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => (
              <div
                key={feature.id}
                className="bg-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow"
              >
                <div className="text-gray-900 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Banner */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                TV Repairs Starting at Just ₹299
              </h2>
              <p className="text-xl text-gray-300 mb-6">
                Affordable, transparent pricing. No hidden charges. Free diagnosis if repaired.
              </p>
              <a
                href="tel:9565711092"
                className="inline-flex items-center px-8 py-4 bg-white text-gray-900 font-bold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                Book Service Now
              </a>
            </div>
            <div className="relative h-80 lg:h-96 rounded-xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/e9vu89f6_1000036533.png"
                alt="Affordable TV Repair - Starting at ₹299"
                loading="lazy"
                className="w-full h-full object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Need TV Repair? We're Just a Call Away!
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Same-day doorstep service across all Kanpur. Expert technicians. 120-day warranty.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:9565711092"
              className="inline-flex items-center justify-center px-8 py-4 bg-gray-900 text-white text-lg font-semibold rounded-lg hover:bg-gray-800 transition-colors shadow-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call: 9565711092
            </a>
            <a
              href="tel:9565711092"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 border-2 border-gray-900 text-lg font-semibold rounded-lg hover:bg-gray-50 transition-colors shadow-lg"
            >
              <Phone className="w-5 h-5 mr-2" />
              Call: 9565711092
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;